import { Button, Card, Tooltip } from '@mui/material';
import { Link, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { visitorsKeys } from '../../api/query-keys/visitors';
import { DownloadDiscountedTicketsDialog } from '../organisms/dialog/download-discounted-tickets-dialog';
import VisitorsSearchAndFilters from '../organisms/visitors-search-and-filters';
import { VisitorsTable } from '../organisms/visitors-table';

import { meiliApi } from '@/api/organization-admin/meili';
import { PageContainer } from '@/components/molecules/page-container';
import { PageHeader } from '@/components/molecules/page-header';
import { Pagination } from '@/components/organisms/pagination';
import { useMeilisearchWithUrlParams } from '@/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from '@/hooks/use-dialog';
import { useSortController } from '@/hooks/use-sort-controller';
import { Plus } from '@/icons/plus';
import { Can, withPermissions } from '@/permissions/can';
import { SortOrder } from '@/types/enum/sort-order';

const Visitors = () => {
  const { t } = useTranslation(['visits', 'common']);

  const { query, perPage, page, filter } = useSearch({
    from: '/_layout/visits/',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const [sortController, setSortController] = useSortController({
    sortBy: 'visitExpectedAt',
    sortOrder: SortOrder.DESC,
  });

  const {
    results,
    isLoading,
    isFetching,
    error,
    handleChangePage,
    handleChangeLimit,
    handleChangeFilterValue,
    resetSingleFilter,
    handleChangeQuery,
    handleChangeSort,
    resetFilters,
  } = useMeilisearchWithUrlParams(
    meiliApi.fetchVisitors,
    visitorsKeys.list(page, perPage),
    {
      q: query,
      filter: filter,
    },
    [`${sortController.sortBy}:${sortController.sortOrder}`],
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  const [
    isDownloadDiscountedTicketsDialogOpen,
    handleOpenDownloadDiscountedTicketsDialog,
    handleCloseDownloadDiscountedTicketsDialog,
  ] = useDialog();

  return (
    <PageContainer helmetPageTitle={t('visits:tabVisitors')}>
      <PageHeader
        title={t('visits:titleVisitors')}
        extras={
          <>
            <Can I="downloadDiscountTicketsRaport" a="Visitors" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      color="primary"
                      variant="contained"
                      size="large"
                      sx={{ mr: 2 }}
                      disabled={!allowed}
                      onClick={handleOpenDownloadDiscountedTicketsDialog}>
                      {t('visits:download')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
            <Can I="create" a="Visitors" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      color="primary"
                      component={Link}
                      to="/visits/create"
                      variant="contained"
                      size="large"
                      disabled={!allowed}
                      startIcon={<Plus fontSize="small" />}>
                      {t('visits:create')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          </>
        }
      />
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}>
        <VisitorsSearchAndFilters
          disabled={false}
          handleChangeFilterValue={handleChangeFilterValue}
          filters={filter ?? {}}
          handleChangeQuery={handleChangeQuery}
          resetFilters={resetFilters}
          resetSingleFilter={resetSingleFilter}
          query={query}
        />
        <VisitorsTable
          visits={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message || ''}
          handleChangeSort={handleChangeSort}
          sortController={sortController}
          setSortController={setSortController}
        />
        <Pagination
          changePage={handleChangePage}
          changePerPage={handleChangeLimit}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          isFetching={isFetching}
        />
      </Card>
      <DownloadDiscountedTicketsDialog
        isOpen={isDownloadDiscountedTicketsDialogOpen}
        onClose={handleCloseDownloadDiscountedTicketsDialog}
      />
    </PageContainer>
  );
};

export default withPermissions(permissions => permissions.cannot('read', 'Visitors'), Visitors);
